import { notifyBugsnag } from '@salescore/client-base'
import type { ViewRecordFieldChange } from '@salescore/client-redux'
import type { ViewQueryList, ViewQueryRecordNode } from '@salescore/core'
import { logger } from '@salescore/frontend-common'
import { Result } from 'effect'

import { VIEW_NEW_RECORD_PREFIX } from '../../domain/constant'
import { getDefaultAttributes } from '../../recoil/records/selectors/querySelector'
import { generateNewRecordId, getNodePure } from '../../state/nodeUtility'
import { type DeepReadonly, toMutable } from '../types/DeepReadonly'
import { findIndexToInsertAfter } from './computeInsertPosition'
import { getParentRecord } from './getParentRecord'
import { replaceChildRecordNode } from './replaceChildRecordNode'

export type CreateNewChildRecordLeft =
  | {
      message:
        | 'レコードを保存してから、子レコードを作成してください。'
        | '参照先を新規作成することはできません'
        | 'このブロックは書き込み禁止です'
        | 'リレーションカラムが存在しません'
    }
  | undefined

/**
 * 新規子レコードを追加したレコードノードを生成する関数（非破壊版）
 *
 * @param attributes - 新規子レコードに設定するattributes
 * @param rootRecordNode - ルートレコードノード
 * @param field - 新規子レコードを設定するnodePathを保持するオブジェクト
 * @param innerRowIndex - 新規子レコードの内部行インデックス
 * @param queryList - 新規子レコードに設定するattributesのデフォルト値を読み出す。
 * @param change - 変更情報。
 *
 * @returns
 *  rootNode - 新規子レコードを追加したルートレコードノード
 *  changes - 新しい要素を加えた後の変更情報の配列
 */
export function createNewChildRecordPure({
  attributes,
  rootRecordNode,
  nodePath,
  innerRowIndex,
  height,
  queryList,
  change,
}: {
  attributes: DeepReadonly<Record<string, unknown>>
  rootRecordNode: DeepReadonly<ViewQueryRecordNode>
  nodePath: DeepReadonly<string[]>
  innerRowIndex: number
  height: number
  queryList: DeepReadonly<Pick<ViewQueryList, 'fields' | 'tree'>>
  change: DeepReadonly<Record<string, unknown>>
}): Result.Result<{ rootNode: ViewQueryRecordNode; newChange: ViewRecordFieldChange }, CreateNewChildRecordLeft> {
  // nodeがhasかbelongsかを確認
  const currentNode = getNodePure(queryList.tree, nodePath)
  if (currentNode?.write?.streamName === undefined) {
    return Result.fail({
      message: 'このブロックは書き込み禁止です',
    })
  }

  if (currentNode.write.parentIdColumn === undefined) {
    // eslint-disable-next-line sonarjs/todo-tag
    // TODO: ViewTree上での子ノードが、DB上での親テーブルに相当するときの分岐
    // このケースの場合は、親テーブルのレコードを新規作成した上で、そのIDをViewTree上での親ノードの参照IDに更新すべきだが、現状これが行えないため、なにも行わない形にする
    return Result.fail({
      message: '参照先を新規作成することはできません',
    })
  }

  // 親のchildrenに対して、レコードを追加する
  const parentRecord = getParentRecord(rootRecordNode, nodePath, innerRowIndex)
  if (parentRecord === undefined) {
    // eslint-disable-next-line sonarjs/todo-tag
    // TODO: そもそもこの条件になるセルは書き込み禁止にする
    logger.warn('SHEET_CELL_EDIT', 'createNewChildRecord で parentRecord が見つかりませんでした')
    return Result.fail(undefined)
  }
  if (parentRecord.id === undefined || parentRecord.id.startsWith(VIEW_NEW_RECORD_PREFIX)) {
    return Result.fail({
      message: 'レコードを保存してから、子レコードを作成してください。',
    })
  }
  const currentNodeName = currentNode.name
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const childRecordNodes = parentRecord.children.find((x) => x.nodeName === currentNodeName)!.children
  const insertIndex = findIndexToInsertAfter(
    innerRowIndex,
    childRecordNodes.map((x) => x.meta),
  )
  // changesの生成
  const joinOn = currentNode.read.join?.joinOn
  const relationColumn = joinOn?.type === 'sql' ? undefined : joinOn?.meta.currentNodePropertyName

  const defaultAttributes = getDefaultAttributes(currentNode, queryList)
  // レコードの作成
  const newChildNodeItem: ViewQueryRecordNode = {
    id: generateNewRecordId(),
    attributes: {
      ...defaultAttributes.recordAttributes,
      ...attributes,
      ...(relationColumn === undefined
        ? {}
        : {
            // relationColumnは厳密にはattributesでないのでrecordNodeに含める必要はないが、changesのみに存在するのが分かりづらいためいれておく。
            // サーバーサイドのレスポンスも同様になっている。
            [`${currentNodeName}_${relationColumn}`]: parentRecord.id,
          }),
    },
    children: [],
    meta: {
      height,
      innerRowIndexStart: innerRowIndex,
      innerRowIndexEnd: innerRowIndex + height,
    },
  }
  const newParentRecord: ViewQueryRecordNode = {
    ...parentRecord,
    children: parentRecord.children.map((child) =>
      child.nodeName === currentNodeName
        ? {
            ...child,
            children: child.children.toSpliced(insertIndex, 0, newChildNodeItem),
          }
        : child,
    ),
  }
  const pathToParent = nodePath.slice(0, -1)
  const newRootNodeResult =
    pathToParent.length === 1
      ? // 2層構造: parentRecordがrootRecordNodeなので、parentRecordをそのまま使う
        Result.succeed(newParentRecord)
      : // 3層以上の構造: replaceChildRecordNodeで親レコードを置き換える
        replaceChildRecordNode(rootRecordNode, pathToParent, newParentRecord)

  if (Result.isFailure(newRootNodeResult)) {
    return Result.fail(newRootNodeResult.failure)
  }
  const newRootNode = toMutable(newRootNodeResult.success)
  if (relationColumn === undefined) {
    notifyBugsnag({ error: new Error(`relationColumn does not exist`) })
    return Result.fail({
      message: 'リレーションカラムが存在しません',
    })
  } else {
    const newChange = {
      id: newChildNodeItem.id,
      streamName: currentNode.write.streamName,
      // eslint-disable-next-line sonarjs/todo-tag
      fieldChanges: [], // TODO 新規子レコード生成時のcallbackはそこまで重要じゃないはずなので後回し
      before: {},
      after: {
        ...defaultAttributes.changeAttributes,
        [relationColumn]: newParentRecord.id,
        ...change,
      },
    }
    return Result.succeed({ rootNode: newRootNode, newChange })
  }
}
